import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, ZIndexProvider, ErrorDialogProvider } from '@/context';
import { Toaster } from 'sonner';

import queryClient from '@/queryClient';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AuthProvider, GoogleAuthProvider } from '@/features/auth';
import { homeRoutes, Home } from '@/features/home/routes';
import { authRoutes } from '@/features/auth/routes';
import { adminRoutes } from '@/features/admin/routes';
import { dashboardRoutes } from '@/features/dashboard/routes';
import { projectsRoutes } from '@/features/projects/routes';
import { mobileRoutes } from '@/features/mobile/routes';
import { privacyRoutes } from '@/features/privacy/routes';
import { countiesRoutes } from '@/features/counties/routes';
import { LoadingSpinner } from '@/routes/utils';
import { RootRedirect } from '@/routes/guards';
import RouteTracker from '@/routes/RouteTracker';
import '@/config/muiLicense';

const AppRoutes: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <RouteTracker />
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<RootRedirect guest={<Home />} />} />

        {homeRoutes}
        {mobileRoutes}
        {privacyRoutes}
        {countiesRoutes}
        {authRoutes}
        {adminRoutes}

        <Route path="/platform" element={<Navigate to="/admin/keys" replace />} />
        <Route path="/platform/keys" element={<Navigate to="/admin/keys" replace />} />
        <Route path="/platform/admin-keys" element={<Navigate to="/admin/api-keys" replace />} />
        <Route path="/platform/people" element={<Navigate to="/admin/people" replace />} />
        <Route path="/platform/usage" element={<Navigate to="/admin/usage" replace />} />

        {dashboardRoutes}
        {projectsRoutes}
      </Routes>
    </Suspense>
  </Router>
);

function App() {
  return (
    <ErrorBoundary>
      <GoogleAuthProvider>
        <ThemeProvider>
          <CssBaseline enableColorScheme />
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ZIndexProvider>
                <ErrorDialogProvider>
                  <Toaster position="bottom-right" richColors />
                  <AppRoutes />
                </ErrorDialogProvider>
              </ZIndexProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </GoogleAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
